import React, { useEffect, useState } from 'react';
import { Button, Modal } from '@vwfs-bronson/bronson-react';
import './style.css';
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  markCustomerForDeletion,
  unmarkCustomerForDeletion,
  fetchCustomerDataById,
  selectCustomer, suspendCustomer, unsuspendCustomer,
} from "../../../redux/slices/customer-slice";
import DetailComponent from "../../base/detail-component";
import {
  BookingStatusEnum,
  PhoneNumberVerificationStatus,
  TripStatusEnum
} from '../../../models/customer-data.interface';
import { ModalLocales, ButtonLocales } from './locales';

export default function PersonalDataView() {
  const customer = useAppSelector(selectCustomer());
  const dispatch = useAppDispatch();
  const [isMarkForDeletionModalOpen, setIsMarkForDeletionModalOpen] = useState(false);
  const [isUnmarkForDeletionModalOpen, setIsUnmarkForDeletionModalOpen] = useState(false);
  const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false);
  const [isUnsuspendModalOpen, setIsUnsuspendModalOpen] = useState(false);
  const [isCustomerSuspendable, setIsCustomerSuspendable] = useState(true);

  const handleUnmarkCustomerForDeletionClick = () => {
    setIsUnmarkForDeletionModalOpen(true);
  };

  const handleConfirmUnmarkForDeletion = async () => {
    setIsUnmarkForDeletionModalOpen(false);
    dispatch(unmarkCustomerForDeletion({ id: customer.customer?.id })).then((res) => {
      if ('fulfilled' === res.meta.requestStatus) {
        dispatch(fetchCustomerDataById({ id: customer.customer?.id }));
      }
    });
  };

  const handleCancelUnmarkForDeletion = () => {
    setIsUnmarkForDeletionModalOpen(false);
  };

  const handleMarkCustomerForDeletionClick = () => {
    setIsMarkForDeletionModalOpen(true);
  };

  const handleConfirmMarkForDeletion = async () => {
    setIsMarkForDeletionModalOpen(false);
    dispatch(markCustomerForDeletion({ id: customer.customer?.id })).then((res) => {

      if ('fulfilled' === res.meta.requestStatus && !res.payload) {
        dispatch(fetchCustomerDataById({ id: customer.customer?.id }));
      }
    });
  };

  const handleCancelMarkForDeletion = () => {
    setIsMarkForDeletionModalOpen(false);
  };

  const handleSuspendClick = () => {
    setIsSuspendModalOpen(true)
  }

  const handleConfirmSuspend = async () => {
    setIsSuspendModalOpen(false);
    dispatch(suspendCustomer({ id: customer.customer?.id })).then((res) => {
      if ('fulfilled' === res.meta.requestStatus && res.payload === '') {
        dispatch(fetchCustomerDataById({ id: customer.customer?.id }));
      }
    });
  };

  const handleCancelSuspend = () => {
    setIsSuspendModalOpen(false);
  };

  const handleUnsuspendClick = () => {
    setIsUnsuspendModalOpen(true)
  }

  const handleConfirmUnsuspend = async () => {
    setIsUnsuspendModalOpen(false);

    dispatch(unsuspendCustomer({ id: customer.customer?.id })).then((res) => {
      if ('fulfilled' === res.meta.requestStatus) {
        dispatch(fetchCustomerDataById({ id: customer.customer?.id }));
      }
    });
  };

  const handleCancelUnsuspend = () => {
    setIsUnsuspendModalOpen(false);
  };

  useEffect(() => {
    let customerSuspendable = true;

    if (customer?.trips?.data?.length > 0) {
      const inTripStates: TripStatusEnum[] = ['IN_STOPOVER', 'IN_TRIP', 'UPCOMING', 'VEHICLE_READY'];
      const index = customer.trips.data.findIndex(trip => inTripStates.includes(trip.state));

      if (index !== -1) {
        customerSuspendable = false;
      }
    }

    if (customer?.bookings?.data?.length > 0) {
      const hasBookingStates: BookingStatusEnum[] = ['NO_SHOW', 'REQUEST_CONFIRMED', 'REQUEST_RECEIVED', 'RESERVED', 'PICKED_UP'];
      const index = customer.bookings.data.findIndex(booking => hasBookingStates.includes(booking.status));

      if (index !== -1) {
        customerSuspendable = false;
      }
    }

    setIsCustomerSuspendable(customerSuspendable);
  }, [customer, customer.trips, customer.bookings]);

  function renderEmailVerifiedStatus(emailVerified: boolean | undefined) {
    return emailVerified ? 'VERIFIED' : 'NOT VERIFIED';
  }

  function renderPhoneNumberVerifiedStatus(status: PhoneNumberVerificationStatus) {
    return status === "APPROVED" ? 'VERIFIED' : 'NOT VERIFIED';
  }

  return (
    <>
      <div>
        <div className='personal-details'>
          <DetailComponent id='customerId' label='Customer ID' value={customer.customer?.id} copyIcon />
          <DetailComponent id='firstName' label='First Name' value={customer.customer?.firstName} />
          <DetailComponent id='lastName' label='Last Name' value={customer.customer?.lastName} />
          <DetailComponent id='language' label='Language' value={customer.customer?.language} />
          <DetailComponent id='driverId' label='Driver ID' value={customer.customer?.driverId} copyIcon/>
          <DetailComponent id='dateOfBirth' label='Date of Birth' value={customer.customer?.dateOfBirth} />
          <DetailComponent id='fiscalCode' label='Fiscal Code' value={customer.customer?.fiscalCode} />
        </div>
        <div className='personal-details'>
          <DetailComponent id='email' label='E-Mail' value={customer.customer?.email} copyIcon />
          <DetailComponent id='emailVerificationStatus' label='E-Mail Verification Status' value={renderEmailVerifiedStatus(customer.customer?.emailVerified)} />
          <DetailComponent id='phoneNumber' label='Phone Number' value={customer.customer?.phoneNumber} copyIcon />
          <DetailComponent id='phoneNumberVerificationStatus' label='Phone Number Verification Status'
            value={renderPhoneNumberVerifiedStatus(customer.customer?.phoneNumberVerificationStatus)} />
        </div>
        <div className='personal-details'>
          <DetailComponent id='countryCode' label='Country Code' value={customer.customer?.address?.countryCode} />
          <DetailComponent id='city' label='City' value={customer.customer?.address?.city} />
          <DetailComponent id='zipCode' label='ZIP Code' value={customer.customer?.address?.zipCode} />
          <DetailComponent id='state' label='State' value={customer.customer?.address?.state} />
          <DetailComponent id='street' label='Street' value={customer.customer?.address?.street} />
          <DetailComponent id='houseNumber' label='House Number' value={customer.customer?.address?.houseNumber} />
          <DetailComponent id='addressExtra' label='Address Extra' value={customer.customer?.address?.addressExtra} />
        </div>
        <div className='personal-details'>
          <DetailComponent id='accountStatus' label='Account Status' value={customer.customer?.accountStatus} />
          <DetailComponent id='personalDetailsStatus' label='Personal Details Status'
            value={customer.customer?.personalDetailsStatus} />

          <DetailComponent id='deletionRequestDate' label='Deletion Request Date'
            value={customer.customer?.deletionRequestDate} />
          <DetailComponent id='createdAt' label='Created At' value={customer.customer?.createdAt} />
          <DetailComponent id='updatedAt' label='Updated At' value={customer.customer?.updatedAt} />
        </div>

        <div>
          <Button
            className='blue-button'
            data-testid='unsuspendButtonTestID'
            onClick={handleUnsuspendClick}
            disabled={customer.customer?.accountStatus !== "SUSPENDED"}
            title={(customer.customer?.accountStatus !== "SUSPENDED" ? ButtonLocales.Unsuspend.title : '')}
          >
            {ButtonLocales.Unsuspend.label}
          </Button>
          <Button
            className='blue-button'
            data-testid='suspendButtonTestID'
            onClick={handleSuspendClick}
            disabled={customer.customer?.accountStatus !== "APPROVED" || !isCustomerSuspendable}
            title={(customer.customer?.accountStatus !== "APPROVED" || !isCustomerSuspendable ? ButtonLocales.Suspend.title : '')}
          >
            {ButtonLocales.Suspend.label}
          </Button>
          <Button
            className='blue-button'
            data-testid='markForDeletionButtonTestID'
            onClick={handleMarkCustomerForDeletionClick}
            disabled={customer.customer?.accountStatus === "MARKED_FOR_DELETION"}
            title={((customer.customer?.accountStatus === "MARKED_FOR_DELETION") ? ButtonLocales.MarkForDeletion.title : '')}
          >
            {ButtonLocales.MarkForDeletion.label}
          </Button>

          <Button
            className='blue-button'
            data-testid='unmarkForDeletionButtonTestID'
            onClick={handleUnmarkCustomerForDeletionClick}
            disabled={customer.customer?.accountStatus !== "MARKED_FOR_DELETION"}
            title={((customer.customer?.accountStatus !== "MARKED_FOR_DELETION") ? ButtonLocales.UnmarkForDeletion.title : '')}
          >
            {ButtonLocales.UnmarkForDeletion.label}
          </Button>
        </div>
      </div >
      <Modal
        shown={isMarkForDeletionModalOpen}
        onClose={handleCancelMarkForDeletion}
        onConfirm={handleConfirmMarkForDeletion}
        onCancel={handleCancelMarkForDeletion}
        buttonCancelLabel="Cancel"
        buttonCancelText="Cancel"
        buttonConfirmLabel="Confirm"
        buttonConfirmText="Confirm"
        title={ModalLocales.MarkForDeletion.title}
      >
        <p>{ModalLocales.MarkForDeletion.message}</p>
      </Modal>
      <Modal
        shown={isUnmarkForDeletionModalOpen}
        onClose={handleCancelUnmarkForDeletion}
        onConfirm={handleConfirmUnmarkForDeletion}
        onCancel={handleCancelUnmarkForDeletion}
        buttonCancelLabel="Cancel"
        buttonCancelText="Cancel"
        buttonConfirmLabel="Confirm"
        buttonConfirmText="Confirm"
        title={ModalLocales.UnmarkForDeletion.title}
      >
        <p>{ModalLocales.UnmarkForDeletion.message}</p>
      </Modal>
      <Modal
        shown={isSuspendModalOpen}
        onClose={handleCancelSuspend}
        onConfirm={handleConfirmSuspend}
        onCancel={handleCancelSuspend}
        buttonCancelLabel="Cancel"
        buttonCancelText="Cancel"
        buttonConfirmLabel="Confirm"
        buttonConfirmText="Confirm"
        title={ModalLocales.SuspendAccount.title}
      >
        <p>{ModalLocales.SuspendAccount.message}</p>
      </Modal>
      <Modal
        shown={isUnsuspendModalOpen}
        onClose={handleCancelUnsuspend}
        onConfirm={handleConfirmUnsuspend}
        onCancel={handleCancelUnsuspend}
        buttonCancelLabel="Cancel"
        buttonCancelText="Cancel"
        buttonConfirmLabel="Confirm"
        buttonConfirmText="Confirm"
        title={ModalLocales.UnsuspendAccount.title}
      >
        <p>{ModalLocales.UnsuspendAccount.message}</p>
      </Modal>
    </>
  );
}
